import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, Shield, Users, Globe, Zap, Lock,
  ArrowRight, ChevronRight, Building2,
  BarChart3, Layers, Eye, AlertTriangle, MessageSquare,
  Target, Phone
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.1 } }
};

const LandingPage = () => {
  return (
    <div className="font-satoshi bg-zinc-50 text-zinc-950 antialiased" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-xl bg-white/70 border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-zinc-950 rounded-sm flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-cabinet font-bold text-lg tracking-tight">NineDegree</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-medium text-zinc-600">
            <a href="#features" className="hover:text-zinc-950 transition-colors">Features</a>
            <a href="#how-it-works" className="hover:text-zinc-950 transition-colors">How It Works</a>
            <Link to="/request-pricing" className="hover:text-zinc-950 transition-colors">Pricing</Link>
            <Link to="/login" className="hover:text-zinc-950 transition-colors">Login</Link>
          </div>
          <Link
            to="/signup"
            className="bg-zinc-950 text-white text-sm font-medium px-5 py-2.5 rounded-sm hover:bg-zinc-800 transition-colors"
            data-testid="nav-start-trial"
          >
            Start Free Trial
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.div variants={fadeUp} className="inline-flex items-center gap-2 bg-zinc-100 border border-zinc-200 px-4 py-1.5 rounded-full text-sm font-medium text-zinc-600 mb-8">
              <Zap className="w-3.5 h-3.5 text-blue-600" />
              Real-time inventory for real estate
            </motion.div>
            <motion.h1 variants={fadeUp} className="font-cabinet font-extrabold text-5xl sm:text-6xl lg:text-[5rem] tracking-tighter leading-none text-zinc-950 mb-6">
              Sell Inventory<br />Without Chaos
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg md:text-xl text-zinc-500 max-w-2xl mx-auto mb-10 leading-relaxed">
              Control availability across sales teams, channel partners, and your website — in real time. No more double bookings. No more Excel.
            </motion.p>
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/signup"
                className="bg-blue-600 text-white text-sm font-semibold px-8 py-3.5 rounded-sm hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                data-testid="hero-start-trial"
              >
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/request-pricing"
                className="border border-zinc-300 text-zinc-700 text-sm font-semibold px-8 py-3.5 rounded-sm hover:bg-zinc-100 transition-colors inline-flex items-center gap-2"
                data-testid="hero-request-pricing"
              >
                Request Pricing <ChevronRight className="w-4 h-4" />
              </Link>
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.7 }}
            className="mt-16 relative"
          >
            <div className="bg-white border border-zinc-200 shadow-2xl shadow-zinc-200/50 rounded-sm overflow-hidden">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/e67041ee-1707-4ed7-b658-fa89b35f4bc9/images/e7ea783e84bb42df529b5266186398872224360062549e694c05333830eb8668.png"
                alt="Dashboard Preview"
                className="w-full"
                loading="lazy"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Problem */}
      <section className="bg-zinc-950 text-zinc-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-blue-400 font-medium text-sm mb-4 tracking-wider uppercase">The Problem</motion.p>
            <motion.h2 variants={fadeUp} className="font-cabinet font-bold text-3xl md:text-4xl lg:text-5xl tracking-tighter">
              Your inventory is out of control
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: AlertTriangle, title: 'Double Booking', desc: 'Same unit sold to two buyers. Lawsuits, refunds, and lost trust.' },
              { icon: MessageSquare, title: 'WhatsApp Chaos', desc: 'Inventory updates shared on WhatsApp groups. Always outdated.' },
              { icon: Eye, title: 'No Visibility', desc: 'Management has no real-time view of what\'s available or blocked.' },
              { icon: Users, title: 'CP Conflicts', desc: 'Channel partners book units without control. No accountability.' },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="border border-zinc-800 p-8 rounded-sm">
                <item.icon className="w-6 h-6 text-red-400 mb-4" />
                <h3 className="font-cabinet font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Solution */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-blue-600 font-medium text-sm mb-4 tracking-wider uppercase">The Solution</motion.p>
            <motion.h2 variants={fadeUp} className="font-cabinet font-bold text-3xl md:text-4xl lg:text-5xl tracking-tighter mb-4">
              One system. Complete control.
            </motion.h2>
            <motion.p variants={fadeUp} className="text-zinc-500 text-lg max-w-2xl mx-auto">
              NineDegree gives you a centralized inventory system with real-time updates and controlled access for every stakeholder.
            </motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Layers, title: 'Central Inventory', desc: 'Single source of truth for all units across projects, towers, and blocks.' },
              { icon: Zap, title: 'Real-Time Updates', desc: 'Status changes reflect instantly. No lag, no conflicts.' },
              { icon: Lock, title: 'Controlled Access', desc: 'Role-based permissions. Sales, CPs, and management see exactly what they should.' },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="bg-white border border-zinc-200 p-10 rounded-sm shadow-sm">
                <div className="w-10 h-10 bg-blue-50 rounded-sm flex items-center justify-center mb-5">
                  <item.icon className="w-5 h-5 text-blue-600" />
                </div>
                <h3 className="font-cabinet font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="bg-zinc-100 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-blue-600 font-medium text-sm mb-4 tracking-wider uppercase">How It Works</motion.p>
            <motion.h2 variants={fadeUp} className="font-cabinet font-bold text-3xl md:text-4xl lg:text-5xl tracking-tighter">
              Three steps to control
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: '01', title: 'Add Projects', desc: 'Create your projects with towers, blocks, and unit configurations in minutes.' },
              { step: '02', title: 'Assign Inventory', desc: 'Map units to sales channels and control who sees what with visibility rules.' },
              { step: '03', title: 'Control Bookings', desc: 'Soft-block, book, and manage unit status with complete audit trail.' },
            ].map((item) => (
              <motion.div key={item.step} variants={fadeUp} className="text-center md:text-left">
                <span className="font-cabinet font-extrabold text-5xl text-zinc-200">{item.step}</span>
                <h3 className="font-cabinet font-bold text-xl mt-4 mb-3">{item.title}</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-blue-600 font-medium text-sm mb-4 tracking-wider uppercase">Features</motion.p>
            <motion.h2 variants={fadeUp} className="font-cabinet font-bold text-3xl md:text-4xl lg:text-5xl tracking-tighter">
              Everything you need to sell better
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Large card */}
            <motion.div variants={fadeUp} className="md:col-span-8 bg-white border border-zinc-200 p-10 rounded-sm shadow-sm">
              <LayoutDashboard className="w-6 h-6 text-blue-600 mb-4" />
              <h3 className="font-cabinet font-bold text-xl mb-2">Inventory Dashboard</h3>
              <p className="text-zinc-500 text-sm leading-relaxed mb-6">Real-time overview of all units — available, blocked, booked. Filter by project, tower, status. Grid, List, and Tower views.</p>
              <img
                src="https://static.prod-images.emergentagent.com/jobs/e67041ee-1707-4ed7-b658-fa89b35f4bc9/images/f5243bd24cfb80dadeffdb5220fcf03f7d8b00b4673b00ba363204486358133a.png"
                alt="Feature visual"
                className="w-full rounded-sm border border-zinc-100"
                loading="lazy"
              />
            </motion.div>
            {/* Small cards stack */}
            <div className="md:col-span-4 flex flex-col gap-6">
              <motion.div variants={fadeUp} className="bg-white border border-zinc-200 p-8 rounded-sm shadow-sm flex-1">
                <Shield className="w-6 h-6 text-blue-600 mb-4" />
                <h3 className="font-cabinet font-bold text-lg mb-2">Soft Blocking</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">Time-bound holds with auto-expiry. No more permanent blocks choking your inventory.</p>
              </motion.div>
              <motion.div variants={fadeUp} className="bg-white border border-zinc-200 p-8 rounded-sm shadow-sm flex-1">
                <Target className="w-6 h-6 text-blue-600 mb-4" />
                <h3 className="font-cabinet font-bold text-lg mb-2">Channel Control</h3>
                <p className="text-zinc-500 text-sm leading-relaxed">Define channels — Internal, Partner, Website. Control unit allocation and price visibility per channel.</p>
              </motion.div>
            </div>
            {/* Bottom row */}
            <motion.div variants={fadeUp} className="md:col-span-4 bg-white border border-zinc-200 p-8 rounded-sm shadow-sm">
              <Users className="w-6 h-6 text-blue-600 mb-4" />
              <h3 className="font-cabinet font-bold text-lg mb-2">CP Portal</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">Mobile-friendly portal for channel partners. Controlled visibility, soft-block only.</p>
            </motion.div>
            <motion.div variants={fadeUp} className="md:col-span-4 bg-white border border-zinc-200 p-8 rounded-sm shadow-sm">
              <Globe className="w-6 h-6 text-blue-600 mb-4" />
              <h3 className="font-cabinet font-bold text-lg mb-2">Public APIs</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">Expose live inventory to your website. Secure API keys per company.</p>
            </motion.div>
            <motion.div variants={fadeUp} className="md:col-span-4 bg-white border border-zinc-200 p-8 rounded-sm shadow-sm">
              <BarChart3 className="w-6 h-6 text-blue-600 mb-4" />
              <h3 className="font-cabinet font-bold text-lg mb-2">Audit Trail</h3>
              <p className="text-zinc-500 text-sm leading-relaxed">Every action logged. Know who changed what, when, and why. Full accountability.</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section id="contact" className="relative bg-zinc-950 py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/e67041ee-1707-4ed7-b658-fa89b35f4bc9/images/83cf4a0bcc433308936ee8e71aed228f5187f29052b3dc5fa88e43ea22faa5a6.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="font-cabinet font-extrabold text-3xl md:text-4xl lg:text-5xl tracking-tighter text-white mb-6">
              Stop managing inventory on Excel
            </motion.h2>
            <motion.p variants={fadeUp} className="text-zinc-400 text-lg max-w-xl mx-auto mb-10">
              Join real estate developers who&apos;ve switched to real-time inventory control. Start your free trial in 30 seconds.
            </motion.p>
            <motion.div variants={fadeUp} className="flex items-center justify-center gap-4 flex-wrap">
              <Link
                to="/signup"
                className="bg-blue-600 text-white text-sm font-semibold px-8 py-3.5 rounded-sm hover:bg-blue-700 transition-colors inline-flex items-center gap-2"
                data-testid="cta-bottom-start"
              >
                Start Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/request-pricing"
                className="border border-zinc-600 text-zinc-300 text-sm font-semibold px-8 py-3.5 rounded-sm hover:bg-zinc-800 transition-colors inline-flex items-center gap-2"
                data-testid="cta-bottom-pricing"
              >
                Request Pricing
              </Link>
              <a
                href="mailto:sales@ninedegree.in"
                className="border border-zinc-600 text-zinc-300 text-sm font-semibold px-8 py-3.5 rounded-sm hover:bg-zinc-800 transition-colors inline-flex items-center gap-2"
              >
                <Phone className="w-4 h-4" /> Talk to Sales
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-zinc-950 border-t border-zinc-800 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-zinc-800 rounded-sm flex items-center justify-center">
              <Building2 className="w-3 h-3 text-zinc-400" />
            </div>
            <span className="font-cabinet font-bold text-sm text-zinc-400">NineDegree</span>
          </div>
          <p className="text-zinc-500 text-xs">
            Built for real estate developers in India. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
